import { useState } from "react";
import { Plus, Search, Users, MoreHorizontal, Award } from "lucide-react";
import { toast } from "sonner";
import { useCustomers, useCreateCustomer, useUpdateCustomer } from "../api";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
} from "@/components/ui";
import {
  PageHeader,
  PageLoader,
  PageError,
  EmptyState,
  PermissionButton,
  PermissionGate,
  DataTable,
  type Column,
} from "@/components/common";
import { formatCurrency } from "@/lib/utils";
import type { Customer } from "@/types";

export function CustomersPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );

  const {
    data: customersData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useCustomers({
    search: search || undefined,
    page,
    limit: pageSize,
  });
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();

  const customers = customersData?.data ?? [];
  const pagination = customersData?.pagination;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleCreate = async (data: {
    name: string;
    phone: string;
    email: string;
  }) => {
    try {
      await createCustomer.mutateAsync(data);
      toast.success("Customer created");
      setIsCreateOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to create customer",
      );
    }
  };

  const handleUpdate = async (data: {
    name: string;
    phone: string;
    email: string;
  }) => {
    if (!selectedCustomer) return;
    try {
      await updateCustomer.mutateAsync({ id: selectedCustomer.id, data });
      toast.success("Customer updated");
      setSelectedCustomer(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update customer",
      );
    }
  };

  const columns: Column<Customer>[] = [
    {
      key: "name",
      header: "Name",
      cell: (customer) => (
        <span className="font-medium">{customer.name}</span>
      ),
    },
    {
      key: "phone",
      header: "Phone",
      cell: (customer) => customer.phone,
    },
    {
      key: "email",
      header: "Email",
      cell: (customer) => customer.email || "-",
    },
    {
      key: "loyaltyPoints",
      header: "Loyalty Points",
      cell: (customer) => (
        <Badge variant="secondary" className="gap-1">
          <Award className="h-3 w-3" />
          {customer.loyaltyPoints}
        </Badge>
      ),
    },
    {
      key: "totalSpent",
      header: "Total Spent",
      cell: (customer) => formatCurrency(customer.totalSpent),
    },
    {
      key: "visitCount",
      header: "Visits",
      cell: (customer) => customer.visitCount,
    },
    {
      key: "actions",
      header: "Actions",
      className: "w-[100px]",
      cell: (customer) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <PermissionGate permission="customers.update">
              <DropdownMenuItem
                onClick={() => setSelectedCustomer(customer)}
              >
                Edit
              </DropdownMenuItem>
            </PermissionGate>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const renderMobileCard = (customer: Customer) => (
    <div className="rounded-lg border p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium">{customer.name}</p>
          <p className="text-sm text-muted-foreground">{customer.phone}</p>
          {customer.email && (
            <p className="truncate text-sm text-muted-foreground">
              {customer.email}
            </p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="shrink-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <PermissionGate permission="customers.update">
              <DropdownMenuItem onClick={() => setSelectedCustomer(customer)}>
                Edit
              </DropdownMenuItem>
            </PermissionGate>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-sm">
        <Badge variant="secondary" className="gap-1">
          <Award className="h-3 w-3" />
          {customer.loyaltyPoints} pts
        </Badge>
        <span className="text-muted-foreground">
          Spent: {formatCurrency(customer.totalSpent)}
        </span>
        <span className="text-muted-foreground">
          {customer.visitCount} visits
        </span>
      </div>
    </div>
  );

  if (isLoading && !customersData) return <PageLoader />;
  if (error)

    return <PageError message="Failed to load customers" onRetry={refetch} />;

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <PageHeader
        title="Customers"
        description="Manage your customer database and loyalty program"
        actions={
          <PermissionButton
            permission="customers.create"
            onClick={() => setIsCreateOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Customer
          </PermissionButton>
        }
      />
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Customers ({pagination?.total || 0})
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search customers..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {customers.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No customers found"
              description={
                search
                  ? "Try adjusting your search"
                  : "Get started by adding a customer"
              }
              action={
                !search && (
                  <PermissionButton
                    permission="customers.create"
                    onClick={() => setIsCreateOpen(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Customer
                  </PermissionButton>
                )
              }
            />
          ) : (
            <DataTable
              data={customers}
              columns={columns}
              rowKey={(customer) => customer.id}
              renderMobileCard={renderMobileCard}
              isFetching={isFetching}
              pagination={{
                page,
                pageSize,
                total: pagination?.total ?? 0,
                onPageChange: setPage,
                onPageSizeChange: (size) => {
                  setPageSize(size);
                  setPage(1);
                },
              }}
            />
          )}
        </CardContent>
      </Card>
      <CustomerFormDialog
        open={isCreateOpen || !!selectedCustomer}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateOpen(false);
            setSelectedCustomer(null);
          }
        }}
        customer={selectedCustomer}
        onSubmit={selectedCustomer ? handleUpdate : handleCreate}
        isLoading={createCustomer.isPending || updateCustomer.isPending}
      />
    </div>
  );
}

interface CustomerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer | null;
  onSubmit: (data: { name: string; phone: string; email: string }) => void;
  isLoading: boolean;
}

function CustomerFormDialog({
  open,
  onOpenChange,
  customer,
  onSubmit,
  isLoading,
}: CustomerFormDialogProps) {
  const [formData, setFormData] = useState({
    name: customer?.name || "",
    phone: customer?.phone || "",
    email: customer?.email || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {customer ? "Edit Customer" : "Add Customer"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              required
              disabled={!!customer}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
